export interface CountyData {
  slug: string;
  name: string;
  title: string;
  description: string;
  tagline: string;
  intro: string;
  crops: string[];
  water: string;
  towns: string[];
  priceNote: string;
  highlights: { title: string; body: string }[];
}

export const counties: CountyData[] = [
  {
    slug: "merced-county",
    name: "Merced County",
    title: "Farms & Land for Sale in Merced County, CA | Samran Real Estate",
    description:
      "Buying or selling farmland in Merced County? Gurbir Samran covers almond orchards, row crop ground, and pasture across Merced, Atwater, Los Banos, and Livingston.",
    tagline: "Almonds, dairy ground, and irrigated row crop in the northern Valley",
    intro:
      "Merced County sits at the northern end of California's prime nut-growing belt, where Merced Irrigation District surface water, deep alluvial soils, and a long-established dairy economy have kept farmland demand steady. It is one of the more accessible entry points into Central Valley agriculture — good ground here still trades below comparable Fresno and Madera acreage.",
    crops: [
      "Almonds",
      "Sweet potatoes",
      "Alfalfa and silage corn",
      "Dairy and pasture ground",
      "Pistachios (west side)",
    ],
    water:
      "Much of the eastern county is served by Merced Irrigation District, which historically carries stronger surface allocations than the west side. West-side parcels lean more heavily on groundwater and sit in basins under active SGMA pumping restrictions — always confirm the GSA and its allocation schedule before writing an offer.",
    towns: ["Merced", "Atwater", "Livingston", "Los Banos", "Dos Palos", "Gustine", "Le Grand"],
    priceNote:
      "Mature almond orchards with district water command the top of the county range; open ground and dryland pasture on the west side sits well below it. We price every property against recent comparable sales rather than a per-acre rule of thumb.",
    highlights: [
      {
        title: "Strong surface water east of Highway 99",
        body: "MID-served ground is some of the most reliably irrigated farmland in the northern Valley, which protects value through drought cycles.",
      },
      {
        title: "Entry-level acreage still available",
        body: "Smaller 20–80 acre blocks come to market more often here than in Fresno County, making it a common first purchase for new growers.",
      },
      {
        title: "Dairy transitions creating supply",
        body: "Consolidating dairies regularly release forage ground, some of which converts well to permanent crops where water supports it.",
      },
    ],
  },
  {
    slug: "madera-county",
    name: "Madera County",
    title: "Farms & Land for Sale in Madera County, CA | Samran Real Estate",
    description:
      "Madera County farmland specialists. Gurbir Samran helps buyers and sellers with almond, pistachio, and grape ground around Madera, Chowchilla, and Firebaugh.",
    tagline: "The heart of California's almond and pistachio country",
    intro:
      "Madera County is where a large share of the state's almond and pistachio acreage is concentrated, and it is the county we're asked about most. It rewards buyers who do their water homework: groundwater conditions vary sharply from one subbasin to the next, and two neighbouring parcels can carry very different long-term pumping outlooks.",
    crops: [
      "Almonds",
      "Pistachios",
      "Wine and raisin grapes",
      "Figs and stone fruit",
      "Open ground for development",
    ],
    water:
      "The Madera and Chowchilla subbasins are both critically overdrafted and under active SGMA management, with pumping allocations stepping down over the coming years. Madera Irrigation District and Chowchilla Water District ground carries a meaningful premium over pure well-water parcels for exactly this reason.",
    towns: ["Madera", "Chowchilla", "Firebaugh", "Fairmead", "Oakhurst foothills", "Ripperdan"],
    priceNote:
      "Producing pistachio blocks with district water sit at the top of the county range, mature almonds just behind, and open ground with limited water at the bottom. SGMA allocation is now the single largest swing factor in Madera valuations.",
    highlights: [
      {
        title: "Deepest orchard inventory in the region",
        body: "More mature almond and pistachio blocks trade here than anywhere else we work, which means more comparables and more negotiating leverage.",
      },
      {
        title: "SGMA is the whole ballgame",
        body: "We review the parcel's GSA, its allocation curve, and well logs before you commit — the difference between a good buy and a stranded asset.",
      },
      {
        title: "Pistachio economics hold up",
        body: "Pistachios tolerate the county's heavier and more saline ground and use less water per acre, which is why replant demand keeps shifting that direction.",
      },
    ],
  },
  {
    slug: "fresno-county",
    name: "Fresno County",
    title: "Farms & Land for Sale in Fresno County, CA | Samran Real Estate",
    description:
      "Fresno County farm and ranch real estate. Gurbir Samran represents buyers and sellers of almond, pistachio, citrus, and row crop land across the county.",
    tagline: "The most productive agricultural county in the United States",
    intro:
      "Fresno County produces more agricultural value than any other county in the country, and its farmland market reflects that diversity — almonds and pistachios on the west and north, citrus and stone fruit along the eastern foothill belt, and intensive row crop ground in between. Buying well here means matching the crop to the microclimate and the water source.",
    crops: [
      "Almonds and pistachios",
      "Citrus (oranges, mandarins, lemons)",
      "Table and raisin grapes",
      "Stone fruit",
      "Tomatoes, garlic, and row crops",
    ],
    water:
      "Water conditions swing hard across the county. Fresno Irrigation District and Consolidated Irrigation District ground carries reliable surface supply; west-side parcels dependent on Westlands allocations have seen severe cutbacks in dry years. The eastern citrus belt generally has better groundwater than the west side but is not exempt from SGMA.",
    towns: ["Fresno", "Clovis", "Sanger", "Reedley", "Selma", "Kerman", "Firebaugh", "Coalinga", "Orange Cove"],
    priceNote:
      "Producing citrus and nut ground with secure district water sits at the top of the county range; west-side parcels reliant on cut allocations trade at a steep discount. Water reliability, not soil alone, sets the price here.",
    highlights: [
      {
        title: "Crop diversity lowers your risk",
        body: "Fresno is one of the few places you can choose between nuts, citrus, grapes, and row crops on comparable ground — useful when one commodity cycle turns.",
      },
      {
        title: "Foothill citrus belt is undervalued",
        body: "The eastern belt around Orange Cove, Sanger, and Reedley has favourable frost protection and steady demand from packers.",
      },
      {
        title: "Know which side of the county you're on",
        body: "West-side Westlands ground and east-side district ground are effectively two different markets. We price them separately.",
      },
    ],
  },
];

export const getCounty = (slug: string) => counties.find((c) => c.slug === slug);
